<script setup lang="ts">
    import {
        IInteraction,
        IInteractionTranscriptionSegment,
        InteractionAudioMetaData,
        StreamingAvailability,
    } from '@/js/services/api/interaction/types';
    import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
    import TranscriptionSegmentVisualizationCombined from './TranscriptionSegmentVisualizationCombined.vue';
    import {
        BanIcon,
        CheckIcon,
        ChevronDownIcon,
        CircleXIcon,
        GaugeCircleIcon,
        LoaderCircleIcon,
        PauseIcon,
        PlayIcon,
    } from '@lucide/vue';
    import { __ } from '@/js/stores/expressions';
    import { getInteractionAudioMetaData } from '@/js/services/api/interaction/interactionAPI';
    import { http } from '@/js/services/http';
    import { useAudioPlayerStore } from '@/js/stores/audioPlayer';
    import EchoPopover from '../../EchoPopover.vue';
    import EchoButton from '../../EchoButton.vue';
    import { sentimentColor, SENTIMENTS } from '@/js/composables/useSentimentColor';
    import { usePermissions } from '@/js/composables/usePermissions';
    import { PermissionArea, InteractionModulePermissionFlag } from '@/js/consts/permissions';

    const { hasPermission } = usePermissions();

    const canSeeSentimentLevelTextSegment = hasPermission(
        PermissionArea.InteractionModule,
        InteractionModulePermissionFlag.SentimentLevelTextSegment
    );

    const audioPlayerStore = useAudioPlayerStore();

    const emit = defineEmits<{
        (e: 'current-time-update', currentTime: number): void;
    }>();

    const props = defineProps<{
        interaction: IInteraction;
        customerSegments: IInteractionTranscriptionSegment[];
        agentSegments: IInteractionTranscriptionSegment[];
    }>();

    const trackPosition = ref(0);
    const currentTime = ref(0);
    const audioLengthSeconds = ref(0);
    const audioPlayer = ref<HTMLAudioElement | null>(null);
    const playing = ref(false);
    const audioSource = ref<string>('');
    const loading = ref(false);
    const autoplay = ref(false);
    const isChangingTrackPosition = ref(false);
    const audioMetaData = ref<InteractionAudioMetaData | null>(null);
    const audioFileNotFound = ref(false);
    const streamingExpired = ref(false);

    const MAX_TOKEN_RETRIES = 3;
    const tokenRetryCount = ref(0);

    const playbackRates = [0.8, 0.9, 1, 1.1, 1.2, 1.3, 1.4, 1.6, 1.8, 2];

    const progress = computed(() => {
        if (audioLengthSeconds.value === 0) {
            return 0;
        }

        return (currentTime.value / audioLengthSeconds.value) * 100;
    });

    const progressToTime = computed(() => {
        const mins = Math.floor(currentTime.value / 60);
        const secs = Math.floor(currentTime.value % 60);
        return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
    });

    // Driven by metadata only: RecordingLength can be 0 even though the file exists,
    // so the length is taken from the file once loaded.
    // Available shows play, Expired shows the ban icon, anything else shows nothing.
    const showPlayer = computed(
        () =>
            audioMetaData.value?.streamingAvailability === StreamingAvailability.Available ||
            audioMetaData.value?.streamingAvailability === StreamingAvailability.Expired
    );

    const durationToTime = computed(() => {
        if (audioLengthSeconds.value === 0) return '-:--';
        const mins = Math.floor(audioLengthSeconds.value / 60);
        const secs = Math.floor(audioLengthSeconds.value % 60);
        return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
    });

    const togglePlay = () => {
        if (loading.value || streamingExpired.value) {
            return;
        }

        if (audioSource.value === '') {
            audioSource.value = audioMetaData.value?.audioPlaybackUrl || '';
            autoplay.value = true;
        }

        if (audioPlayer.value) {
            if (playing.value) {
                audioPlayer.value.pause();
            } else {
                audioPlayer.value.currentTime = trackPosition.value;
                audioPlayer.value.play();
            }
        }
    };

    const changeTrackPosition = () => {
        isChangingTrackPosition.value = false;

        if (!playing.value) {
            togglePlay();
            return;
        }

        if (audioPlayer.value) {
            audioPlayer.value.pause();
            audioPlayer.value.currentTime = trackPosition.value;
            audioPlayer.value.play();
        }
    };

    const pause = () => {
        if (audioPlayer.value) {
            audioPlayer.value.pause();
        }
    };

    const seekToPosition = (seconds: number) => {
        if (streamingExpired.value) return;
        // Length unknown until the file has loaded, so only clamp when it is known
        const maxSeconds = audioLengthSeconds.value > 0 ? audioLengthSeconds.value : Infinity;
        trackPosition.value = Math.min(Math.max(0, seconds), maxSeconds);

        // Audio not loaded yet: togglePlay() sets the source and autoplays. The
        // audioSource watcher then seeks to trackPosition once the element exists.
        if (audioSource.value === '') {
            togglePlay();
            return;
        }

        if (audioPlayer.value) {
            audioPlayer.value.currentTime = trackPosition.value;
            audioPlayer.value.play();
        }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
        if (['ArrowLeft', 'ArrowRight', 'ArrowDown', 'ArrowUp'].includes(event.key)) {
            isChangingTrackPosition.value = true;
            pause();
        }
    };

    const fetchAudioMetaData = async () => {
        audioFileNotFound.value = false;
        streamingExpired.value = false;
        audioMetaData.value = await getInteractionAudioMetaData(props.interaction.id);

        streamingExpired.value =
            audioMetaData.value?.streamingAvailability === StreamingAvailability.Expired;
        audioSource.value = '';
        audioLengthSeconds.value = audioMetaData.value?.audioFileLengthSeconds || 0;
    };

    const reloadAudioWithFreshToken = async () => {
        await fetchAudioMetaData();

        // fetchAudioMetaData clears audioSource; wait for the <audio> element to
        // unmount before re-setting the source so it is recreated with the fresh
        // token URL and event listeners are not attached twice.
        await nextTick();

        audioSource.value = audioMetaData.value?.audioPlaybackUrl || '';
        autoplay.value = true;
    };

    const handleAudioFileError = (event: Event) => {
        http.get(audioSource.value).catch((error) => {
            if (error.response.data.code === 'TokenExpired') {
                if (tokenRetryCount.value >= MAX_TOKEN_RETRIES) {
                    loading.value = false;
                    return;
                }

                tokenRetryCount.value++;
                loading.value = true;
                reloadAudioWithFreshToken();
            } else if (error.response.data.code === 'InvalidProtectedToken') {
                loading.value = false;
            } else if (error.response.status === 403) {
                loading.value = false;
                streamingExpired.value = true;
            } else if (error.response.status === 404 || error.response.status === 422) {
                window.setTimeout(() => {
                    loading.value = false;
                    audioFileNotFound.value = true;
                }, 1000);
            }
        });
    };

    watch(
        () => audioSource.value,
        () => {
            nextTick().then(() => {
                if (audioPlayer.value) {
                    audioPlayer.value.playbackRate = audioPlayerStore.playbackRate;

                    if (trackPosition.value > 0) {
                        audioPlayer.value.currentTime = trackPosition.value;
                    }

                    audioPlayer.value.addEventListener('timeupdate', () => {
                        if (audioPlayer.value) {
                            currentTime.value = audioPlayer.value.currentTime;

                            if (!isChangingTrackPosition.value) {
                                trackPosition.value = audioPlayer.value.currentTime;
                            }

                            emit('current-time-update', currentTime.value);
                        }
                    });

                    audioPlayer.value.addEventListener('loadstart', () => {
                        loading.value = true;
                    });

                    // The file is the source of truth for the length, RecordingLength may be wrong
                    audioPlayer.value.addEventListener('loadedmetadata', () => {
                        const duration = audioPlayer.value?.duration ?? 0;
                        if (Number.isFinite(duration) && duration > 0) {
                            audioLengthSeconds.value = duration;
                        }
                    });

                    audioPlayer.value.addEventListener('loadeddata', () => {
                        loading.value = false;
                        tokenRetryCount.value = 0;
                    });

                    audioPlayer.value.addEventListener('ended', () => {
                        playing.value = false;
                        trackPosition.value = 0;
                        currentTime.value = 0;
                        loading.value = false;
                        audioSource.value = '';
                    });

                    audioPlayer.value.addEventListener('play', () => {
                        playing.value = true;
                    });

                    audioPlayer.value.addEventListener('pause', () => {
                        playing.value = false;
                    });
                }
            });
        }
    );

    watch(
        () => audioPlayerStore.playbackRate,
        (rate) => {
            if (audioPlayer.value) {
                audioPlayer.value.playbackRate = rate;
            }
        }
    );

    watch(
        () => audioPlayerStore.pendingSeeks.get(props.interaction.id) ?? null,
        (seconds) => {
            if (seconds === null) {
                return;
            }

            seekToPosition(seconds);
            audioPlayerStore.clearSeek(props.interaction.id);
        }
    );

    // Whether this player actually renders playback controls. Mirror this into the
    // store so the transcript's "listen from here" buttons only show while this
    // interaction's player is present to consume the seek.
    const hasPlaybackControls = computed(() => showPlayer.value && !streamingExpired.value);

    watch(
        hasPlaybackControls,
        (hasControls, hadControls) => {
            if (hasControls && !hadControls) {
                audioPlayerStore.registerPlayer(props.interaction.id);
            } else if (!hasControls && hadControls) {
                audioPlayerStore.unregisterPlayer(props.interaction.id);
            }
        },
        { immediate: true }
    );

    onBeforeUnmount(() => {
        if (hasPlaybackControls.value) {
            audioPlayerStore.unregisterPlayer(props.interaction.id);
        }
    });

    onMounted(() => {
        fetchAudioMetaData();
    });
</script>

<template>
    <div class="flex flex-col gap-2.5">
        <audio
            v-if="audioSource"
            ref="audioPlayer"
            crossorigin="anonymous"
            :autoplay="autoplay"
            preload="metadata"
            :src="audioSource"
            @error="handleAudioFileError($event)"
        />

        <TranscriptionSegmentVisualizationCombined
            v-if="showPlayer"
            :customer-segments="customerSegments"
            :agent-segments="agentSegments"
            :duration="audioLengthSeconds"
            :current-time="currentTime"
            :has-sentiment-data="canSeeSentimentLevelTextSegment"
        >
            <template #before>
                <button
                    type="button"
                    class="ap-play"
                    :aria-label="loading ? __('loading') : playing ? __('pause') : __('play')"
                    @click="togglePlay"
                >
                    <CircleXIcon v-if="audioFileNotFound" class="size-4" />
                    <BanIcon v-else-if="streamingExpired" class="size-4" />
                    <template v-else>
                        <LoaderCircleIcon v-show="loading" class="size-4 animate-spin" />
                        <PauseIcon v-show="!loading && playing" class="size-4" />
                        <PlayIcon v-show="!loading && !playing" class="size-4" />
                    </template>
                </button>
            </template>

            <div
                v-if="canSeeSentimentLevelTextSegment"
                class="ap-playhead"
                :style="{ transform: 'translateX(' + progress + '%)' }"
            />
            <template v-else>
                <div class="ap-flat-fill" :style="{ width: progress + '%' }" />
                <div class="ap-flat-knob" :style="{ left: progress + '%' }" />
            </template>
            <input
                v-model="trackPosition"
                type="range"
                :min="0"
                :max="audioLengthSeconds"
                :step="1"
                class="ap-scrubber"
                :aria-label="__('transcription-audio-player-description')"
                @mouseup="changeTrackPosition()"
                @touchend="changeTrackPosition()"
                @mousedown="isChangingTrackPosition = true"
                @touchstart="isChangingTrackPosition = true"
                @keyup.enter="changeTrackPosition()"
                @keydown="handleKeyDown($event)"
            />
        </TranscriptionSegmentVisualizationCombined>

        <div v-if="showPlayer" class="mt-3 text-body">
            <span v-if="audioFileNotFound" class="text-sentiment-neutral-to-negative">
                {{ __('transcription-audio-player-audio-file-not-found') }}
            </span>
            <span v-else-if="streamingExpired" class="text-sentiment-neutral-to-negative">
                {{ __('transcription-audio-player-streaming-expired') }}
            </span>
            <div v-else class="flex items-center gap-4.5 flex-wrap">
                <span class="text-caption text-gray-dark">
                    {{ progressToTime }} / {{ durationToTime }}
                </span>

                <EchoPopover type="menu" position="top-start">
                    <template #button="{ anchor, togglePopover, buttonAttrs }">
                        <button
                            :ref="anchor"
                            type="button"
                            class="ap-speed-button"
                            v-bind="buttonAttrs"
                            @click="togglePopover"
                        >
                            <GaugeCircleIcon class="size-3.5" />
                            {{
                                __('transcription-audio-player-playback-speed', {
                                    rate: String(audioPlayerStore.playbackRate),
                                })
                            }}
                            <ChevronDownIcon class="size-3.5" />
                        </button>
                    </template>
                    <template #popover="{ closePopover }">
                        <EchoButton
                            v-for="rate in playbackRates"
                            :key="rate"
                            variant="block"
                            size="small"
                            @click="
                                audioPlayerStore.playbackRate = rate;
                                closePopover();
                            "
                        >
                            <span
                                :class="
                                    rate === audioPlayerStore.playbackRate
                                        ? 'text-primary-beet text-body-bold'
                                        : ''
                                "
                            >
                                {{ rate }}x
                            </span>
                            <template v-if="rate === audioPlayerStore.playbackRate" #icon-right>
                                <CheckIcon />
                            </template>
                        </EchoButton>
                    </template>
                </EchoPopover>

                <div v-if="canSeeSentimentLevelTextSegment" class="ap-legend">
                    <span>{{ __('sentiment-negative') }}</span>
                    <span
                        v-for="sentiment in SENTIMENTS"
                        :key="sentiment"
                        class="block h-1.5 w-3 rounded-full"
                        :class="sentimentColor(sentiment)"
                    />
                    <span>{{ __('sentiment-positive') }}</span>
                </div>
            </div>
        </div>
    </div>
</template>
